/**
 * Pure ordering helpers for the per-folder custom-status slice
 * (customizar-status-tarefas). Extracted from `store/mutations.js` so the
 * mutations stay lean and the ordering logic is independently testable.
 *
 * Statuses are kept sorted by `position` (ASC, null/undefined last) so
 * consumers never re-sort.
 */

/**
 * Returns a new array sorted by `position` ASC. Items without a `position`
 * (null/undefined) sort last. Non-array inputs yield an empty array.
 * @param {Array<object>} list
 * @returns {Array<object>}
 */
export function sortStatusesByPosition(list) {
  if (!Array.isArray(list)) return []
  return list.slice().sort(function (a, b) {
    const posA = a && a.position != null ? a.position : Number.MAX_SAFE_INTEGER
    const posB = b && b.position != null ? b.position : Number.MAX_SAFE_INTEGER
    return posA - posB
  })
}

/**
 * Builds the reordered status list from `orderedIds`, appending any statuses
 * not present in the id list at the tail (defensive against partial payloads),
 * and rewrites each `position` to its index (0..N-1).
 * @param {Array<object>} current - the folder's current status list
 * @param {Array<string>} orderedIds - status ids in the desired order
 * @returns {Array<object>}
 */
export function reorderStatusList(current, orderedIds) {
  const safeCurrent = Array.isArray(current) ? current : []
  const safeOrderedIds = Array.isArray(orderedIds) ? orderedIds : []
  const byId = {}
  for (const status of safeCurrent) {
    if (status && status.id != null) byId[status.id] = status
  }
  const reordered = []
  for (const id of safeOrderedIds) {
    if (byId[id]) {
      reordered.push(byId[id])
      delete byId[id]
    }
  }
  for (const status of safeCurrent) {
    if (status && status.id != null && byId[status.id]) reordered.push(status)
  }
  return reordered.map(function (status, index) {
    return { ...status, position: index }
  })
}
